// AssemblyWizard.jsx — "Assemblies & trace": the estimator's order of work.
//
//   1. Pick the sheets that hold the assemblies (wall / partition / ceiling types — usually the
//      typical-details or assembly-schedule sheets).
//   2. Read them → a list of types (tag, name, build-up). Tick the ones to trace.
//   3. Pick the floor plans (walls, with each floor's wall height) and the reflected ceiling plans
//      (ceiling areas). Choose walls, room areas, or both.
//   4. Trace → walls are drawn on the floor plans per wall-type tag (doors left out, windows kept in the
//      run and deducted by area), ceilings are drawn room by room on the RCPs — every room, typed by its
//      tag/callout, else by the grid drawn in it (2'×4', 2'×2', none = drywall), else "Untagged ceiling".
//
// Pure UI: reading pages and running the trace are done by the callbacks the viewer passes in.
import React, { useEffect, useMemo, useState } from "react";
import { gridOfText } from "../lib/takeoff/engine/ceilingGrid";

const KIND_STYLE = {
  wall: "bg-orange-900/50 text-orange-300",
  ceiling: "bg-sky-900/50 text-sky-300",
  floor: "bg-gray-700 text-gray-300",
  roof: "bg-gray-700 text-gray-300",
  other: "bg-gray-700 text-gray-300",
};
const uid = () => Math.random().toString(36).slice(2, 10);

// ── tracing animation: a little plan drawing itself, a scan line, the steps so far and a progress bar ──
const TRACE_CSS = `
@keyframes tw-draw { from { stroke-dashoffset: var(--len); } to { stroke-dashoffset: 0; } }
@keyframes tw-scan { 0% { transform: translateY(-8px); opacity: 0; } 10% { opacity: .9; } 90% { opacity: .9; } 100% { transform: translateY(150px); opacity: 0; } }
@keyframes tw-pop { 0%, 60% { transform: scale(0); opacity: 0; } 80% { transform: scale(1.35); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
@keyframes tw-fill { 0%, 55% { opacity: 0; } 100% { opacity: .18; } }
@keyframes tw-stripes { from { background-position: 0 0; } to { background-position: 28px 0; } }
.tw-line { stroke-dasharray: var(--len); stroke-dashoffset: var(--len); animation: tw-draw 1.1s ease-in-out forwards; }
.tw-loop { animation-iteration-count: infinite; }
`;
const PLAN_WALLS = [
  // [x1, y1, x2, y2, delay s]  — a small floor plan, drawn wall by wall
  [10, 10, 230, 10, 0],
  [230, 10, 230, 140, 0.35],
  [230, 140, 10, 140, 0.7],
  [10, 140, 10, 10, 1.05],
  [95, 10, 95, 62, 1.4],
  [95, 88, 95, 140, 1.55],
  [95, 75, 230, 75, 1.8],
  [160, 75, 160, 140, 2.1],
];
const PLAN_DOORS = [
  [95, 75, 2.5],
  [160, 108, 2.7],
  [196, 75, 2.9],
];
function TraceAnimation({ running, log }) {
  const pct = running.total
    ? Math.min(100, Math.round((running.done / running.total) * 100))
    : 0;
  // restart the drawing for each new sheet so it keeps moving on long traces
  const cycle = `${running.done}-${(log || []).length}`;
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-950/85 backdrop-blur-sm">
      <style>{TRACE_CSS}</style>
      <div className="w-full max-w-md rounded-2xl border border-orange-500/30 bg-gray-900 p-5 shadow-2xl">
        <div className="relative mx-auto mb-4 h-[150px] w-[240px] overflow-hidden rounded-lg border border-white/5 bg-gray-950">
          <svg
            key={cycle}
            viewBox="0 0 240 150"
            className="absolute inset-0 h-full w-full"
          >
            {/* faint "drawing" underneath */}
            {PLAN_WALLS.map(([x1, y1, x2, y2], i) => (
              <line
                key={`g${i}`}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#374151"
                strokeWidth="5"
              />
            ))}
            <rect
              x="10"
              y="10"
              width="85"
              height="130"
              fill="#f97316"
              style={{ animation: "tw-fill 2.4s ease-out forwards" }}
            />
            <rect
              x="160"
              y="75"
              width="70"
              height="65"
              fill="#38bdf8"
              style={{ animation: "tw-fill 3s ease-out forwards" }}
            />
            {PLAN_WALLS.map(([x1, y1, x2, y2, d], i) => {
              const len = Math.hypot(x2 - x1, y2 - y1);
              return (
                <line
                  key={i}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={i < 4 ? "#f97316" : "#a78bfa"}
                  strokeWidth="3"
                  strokeLinecap="round"
                  className="tw-line"
                  style={{ "--len": len, animationDelay: `${d}s` }}
                />
              );
            })}
            {PLAN_DOORS.map(([x, y, d], i) => (
              <circle
                key={`d${i}`}
                cx={x}
                cy={y}
                r="5"
                fill="#e879f9"
                style={{
                  transformOrigin: `${x}px ${y}px`,
                  animation: `tw-pop .6s ease-out ${d}s both`,
                }}
              />
            ))}
          </svg>
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-orange-400/0 via-orange-400/40 to-orange-400/0"
            style={{ animation: "tw-scan 2.2s linear infinite" }}
          />
        </div>
        <div className="mb-1 flex items-baseline justify-between text-xs">
          <span className="font-semibold text-white">Tracing…</span>
          <span className="text-gray-400">
            {running.done}/{running.total} sheets
          </span>
        </div>
        <div className="mb-3 h-2 overflow-hidden rounded-full bg-gray-800">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${Math.max(4, pct)}%`,
              backgroundImage:
                "repeating-linear-gradient(45deg,#f97316 0 7px,#fb923c 7px 14px)",
              backgroundSize: "28px 100%",
              animation: "tw-stripes .8s linear infinite",
            }}
          />
        </div>
        <ul className="max-h-32 space-y-1 overflow-y-auto text-[11px]">
          {(log || []).map((m, i, arr) => (
            <li key={i} className="flex items-center gap-2">
              {i < arr.length - 1 ? (
                <span className="text-green-400">✓</span>
              ) : (
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
              )}
              <span
                className={
                  i < arr.length - 1 ? "text-gray-500" : "text-gray-200"
                }
              >
                {m}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
// every assembly has a short NAME (what the estimator calls it) and a DESCRIPTION (everything in it, one
// item per line). Older / hand-added types without one get it from their layers.
export const descOf = (a) =>
  String(a?.description || "").trim() ||
  (a?.layers || []).filter(Boolean).join("\n");

export const suggestSheetKind = (label, text) => {
  const t = `${label || ""} ${text || ""}`.toUpperCase();
  if (/\bDEMO(LITION)?\b/.test(label || "")) return "other";
  if (/REFLECTED CEILING|\bRCP\b|CEILING PLAN/.test(t)) return "rcp";
  if (
    /ASSEMBL(Y|IES) SCHEDULE|WALL TYPES|PARTITION TYPES|CEILING TYPES|TYPICAL (WALL|PARTITION)|WALL ASSEMBL|ASSEMBLIES/.test(
      t,
    )
  )
    return "assembly";
  if (
    /FLOOR PLAN|BASEMENT PLAN|LEVEL \d+ PLAN|GROUND FLOOR|SECOND FLOOR|THIRD FLOOR|CELLAR PLAN|MEZZANINE PLAN/.test(
      t,
    )
  )
    return "floor";
  return "other";
};

// "BASEMENT RCP" ↔ "BASEMENT FLOOR PLAN"
const levelKey = (s) => {
  const t = String(s || "").toUpperCase();
  if (/BASEMENT|CELLAR|LOWER LEVEL|\bP1\b/.test(t)) return "B";
  if (/GROUND|FIRST|LEVEL ?1\b|MAIN FLOOR/.test(t)) return "1";
  const m = t.match(/(SECOND|THIRD|FOURTH|FIFTH)|LEVEL ?(\d+)/);
  if (m)
    return m[2] || { SECOND: "2", THIRD: "3", FOURTH: "4", FIFTH: "5" }[m[1]];
  return null;
};

export default function AssemblyWizard({
  open,
  onClose,
  pageCount,
  pageLabel,
  readSheetText,
  readAssemblies,
  scaleStatus,
  assemblies,
  setAssemblies,
  settings,
  setSettings,
  onRun,
  onGoToPage,
  parseFeetInput,
  fmtFeetIn,
}) {
  const [step, setStep] = useState(1);
  const [kinds, setKinds] = useState({}); // page → assembly | floor | rcp | other (suggested)
  const [scanning, setScanning] = useState(false);
  const [reading, setReading] = useState("");
  const [readErr, setReadErr] = useState("");
  const [aiNote, setAiNote] = useState("");
  const [running, setRunning] = useState(null); // { done, total, msg }
  const [runLog, setRunLog] = useState([]); // the steps so far, for the tracing animation
  useEffect(() => {
    if (!running?.msg) return;
    setRunLog((prev) =>
      prev[prev.length - 1] === running.msg
        ? prev
        : [...prev, running.msg].slice(-30),
    );
  }, [running?.msg]);
  const [result, setResult] = useState(null);
  const [scales, setScales] = useState({}); // page → { ok, label, source }
  const [openRow, setOpenRow] = useState(null);

  const S = settings || {};
  const set = (patch) => setSettings((prev) => ({ ...(prev || {}), ...patch }));
  const pages = useMemo(
    () => Array.from({ length: pageCount }, (_, i) => i + 1),
    [pageCount],
  );

  // suggest what each sheet is, from its title + text (cheap: text layer only)
  useEffect(() => {
    if (!open || !pageCount || Object.keys(kinds).length) return;
    let dead = false;
    (async () => {
      setScanning(true);
      const out = {};
      for (const n of pages) {
        if (dead) return;
        try {
          const txt = await readSheetText(n, { quick: true });
          out[n] = suggestSheetKind(pageLabel(n), txt.slice(0, 4000));
        } catch {
          out[n] = "other";
        }
      }
      if (dead) return;
      setKinds(out);
      setScanning(false);
      // first time: pre-tick the suggestions
      if (!S.assemblyPages) {
        const floor = {},
          rcp = {};
        for (const n of pages) {
          if (out[n] === "floor")
            floor[n] = {
              on: true,
              height: S.defaultHeight || "",
              walls: true,
              areas: false,
            };
          if (out[n] === "rcp") rcp[n] = { on: true, from: "self" };
        }
        set({
          assemblyPages: pages.filter((n) => out[n] === "assembly"),
          floor,
          rcp,
        });
      }
    })();
    return () => {
      dead = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pageCount]);

  // the assembly sheet often states the wall height ("WALL HEIGHT 9'-0" …") — start from it
  useEffect(() => {
    if (!open || step !== 3 || S.defaultHeight) return;
    const stated = (assemblies || [])
      .filter((a) => a.kind === "wall" && a.trace)
      .map((a) => parseFeetInput(a.height || ""))
      .find((h) => h && h > 6 && h < 40);
    if (!stated) return;
    const txt = fmtFeetIn(stated),
      f = { ...(S.floor || {}) };
    for (const k of Object.keys(f))
      if (!f[k].height) f[k] = { ...f[k], height: txt };
    set({ defaultHeight: txt, floor: f });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step]);

  useEffect(() => {
    if (!open || step !== 3) return;
    let dead = false;
    (async () => {
      const want = [
        ...Object.keys(S.floor || {}),
        ...Object.keys(S.rcp || {}),
      ].map(Number);
      const out = {};
      for (const n of want) {
        try {
          out[n] = await scaleStatus(n);
        } catch {
          out[n] = { ok: false };
        }
      }
      if (!dead) setScales(out);
    })();
    return () => {
      dead = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    step,
    JSON.stringify(Object.keys(S.floor || {})),
    JSON.stringify(Object.keys(S.rcp || {})),
  ]);

  if (!open) return null;

  const asmPages = S.assemblyPages || [];
  const togglePage = (n) =>
    set({
      assemblyPages: asmPages.includes(n)
        ? asmPages.filter((x) => x !== n)
        : [...asmPages, n].sort((a, b) => a - b),
    });

  async function doRead() {
    if (!asmPages.length)
      return setReadErr("Pick at least one sheet with the assemblies.");
    setReadErr("");
    setAiNote("");
    setReading("Reading the sheets…");
    try {
      const data = await readAssemblies(asmPages, (msg) => setReading(msg));
      const prev = new Map(
        (assemblies || []).map((a) => [`${a.kind}|${a.tag}|${a.name}`, a]),
      );
      const next = (data.assemblies || []).map((a) => {
        const old = prev.get(`${a.kind}|${a.tag}|${a.name}`);
        return {
          id: old?.id || uid(),
          trace: old?.trace ?? (a.kind === "wall" || a.kind === "ceiling"),
          conditionId: old?.conditionId || null,
          ...a,
        };
      });
      // keep types the estimator added by hand
      const manual = (assemblies || []).filter((a) => a.source === "manual");
      setAssemblies([...next, ...manual]);
      if (data.aiSkipped)
        setAiNote(
          `AI reading skipped (${data.aiSkipped}) — showing what the text layer gives.`,
        );
      setStep(2);
    } catch (e) {
      setReadErr(e.message || "Could not read the sheets.");
    } finally {
      setReading("");
    }
  }

  const upd = (id, patch) =>
    setAssemblies((prev) =>
      prev.map((a) => (a.id === id ? { ...a, ...patch } : a)),
    );
  const wallTypes = (assemblies || []).filter((a) => a.kind === "wall");
  const ceilTypes = (assemblies || []).filter((a) => a.kind === "ceiling");
  const selWalls = wallTypes.filter((a) => a.trace);
  const selCeils = ceilTypes.filter((a) => a.trace);

  const floorPages = Object.entries(S.floor || {}).map(([n, v]) => ({
    n: Number(n),
    ...v,
  }));
  const rcpPages = Object.entries(S.rcp || {}).map(([n, v]) => ({
    n: Number(n),
    ...v,
  }));
  const setFloor = (n, patch) =>
    set({
      floor: { ...(S.floor || {}), [n]: { ...(S.floor?.[n] || {}), ...patch } },
    });
  const setRcp = (n, patch) =>
    set({
      rcp: { ...(S.rcp || {}), [n]: { ...(S.rcp?.[n] || {}), ...patch } },
    });
  const toggleFloor = (n) => {
    const f = { ...(S.floor || {}) };
    if (f[n]) delete f[n];
    else
      f[n] = {
        on: true,
        height: S.defaultHeight || "",
        walls: true,
        areas: false,
      };
    set({ floor: f });
  };
  const toggleRcp = (n) => {
    const r = { ...(S.rcp || {}) };
    if (r[n]) delete r[n];
    else {
      const lk = levelKey(pageLabel(n));
      const match =
        lk &&
        Object.keys(S.floor || {})
          .map(Number)
          .find((m) => levelKey(pageLabel(m)) === lk);
      r[n] = { on: true, from: match ? String(match) : "self" };
    }
    set({ rcp: r });
  };

  const heightOf = (p) => parseFeetInput(p.height || S.defaultHeight || "");
  const problems = [
    ...floorPages
      .filter((p) => p.on && (p.walls ?? true) && !heightOf(p))
      .map((p) => `${pageLabel(p.n)}: no wall height`),
    ...[...floorPages, ...rcpPages]
      .filter((p) => p.on && scales[p.n] && !scales[p.n].ok)
      .map((p) => `${pageLabel(p.n)}: no scale — calibrate it first`),
  ];
  const canRun =
    (floorPages.some((p) => p.on) || rcpPages.some((p) => p.on)) &&
    !problems.length &&
    (selWalls.length ||
      selCeils.length ||
      S.untaggedWall ||
      rcpPages.some((p) => p.on) ||
      floorPages.some((p) => p.areas));
  // which ceiling type each drawn grid becomes ("auto" = the type whose name/build-up says that grid)
  const GRID_ROWS = [
    ["2x4", "2'×4' grid"],
    ["2x2", "2'×2' grid"],
    ["none", "No grid (drywall)"],
  ];
  const autoGridType = (kind) => {
    const hits = ceilTypes.filter(
      (a) =>
        gridOfText([a.tag, a.name, ...(a.layers || [])].join(" ")) === kind,
    );
    return hits.find((a) => a.trace) || hits[0] || null;
  };
  const gridChoice = (kind) => (S.ceilGrid || {})[kind] ?? "auto";

  async function run() {
    setRunLog([]);
    setRunning({ done: 0, total: 1, msg: "Starting…" });
    try {
      const plan = {
        floor: floorPages
          .filter((p) => p.on)
          .map((p) => ({
            page: p.n,
            height: heightOf(p),
            walls: p.walls ?? true,
            areas: !!p.areas,
          })),
        rcp: rcpPages
          .filter((p) => p.on)
          .map((p) => ({
            page: p.n,
            from: p.from === "self" ? null : Number(p.from),
          })),
        wallTypes: selWalls,
        ceilingTypes: selCeils,
        untaggedWall: S.untaggedWall || "",
        areaTarget: S.areaTarget || "",
        ceilGrid: Object.fromEntries(
          GRID_ROWS.map(([k]) => {
            const v = gridChoice(k);
            return [k, v === "auto" ? autoGridType(k)?.id || "" : v];
          }),
        ),
        windowMode: S.windowMode || "deduct",
        windowHeight: parseFeetInput(S.windowHeight || "") || null,
        doorMode: S.doorMode || "deduct",
        // every door is deducted / counted at these sizes (not the drawn opening)
        doorHeight: parseFeetInput(S.doorHeight || "") || 7,
        doorWidth: parseFeetInput(S.doorWidth || "") || 38 / 12,
        doubleDoorWidth: parseFeetInput(S.doubleDoorWidth || "") || 74 / 12,
        wallLines: S.wallLines || "centre",
        wetSeparate: S.wetSeparate ?? true,
        counts: S.counts ?? true,
        cornerBead: S.cornerBead ?? true,
        openFaces: S.openFaces ?? true,
        aiWalls: S.aiWalls ?? true,
        aiOpenings: S.aiOpenings ?? true,
      };
      const res = await onRun(plan, (p) => setRunning(p));
      setResult(res);
      setStep(4);
    } catch (e) {
      setResult({ error: e.message || String(e) });
      setStep(4);
    } finally {
      setRunning(null);
    }
  }

  const Step = ({ n, label }) => (
    <button
      onClick={() => setStep(n)}
      disabled={n === 4 && !result}
      className={`flex items-center gap-2 rounded px-2 py-1 text-xs ${step === n ? "bg-orange-600 text-white" : "text-gray-400 hover:bg-gray-800 disabled:opacity-40"}`}
    >
      <span
        className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${step === n ? "bg-white text-orange-700" : "bg-gray-700 text-gray-300"}`}
      >
        {n}
      </span>
      {label}
    </button>
  );
  const kindBadge = (k) => (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${KIND_STYLE[k] || KIND_STYLE.other}`}
    >
      {k}
    </span>
  );

  return (
    <div className="absolute inset-0 z-[1200] flex items-start justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-sm">
      <div className="relative flex max-h-full w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-gray-900 shadow-2xl">
        {running && <TraceAnimation running={running} log={runLog} />}
        <div className="flex items-center gap-2 border-b border-white/5 px-5 py-3.5">
          <h2 className="text-sm font-semibold text-white">
            Assemblies &amp; trace
          </h2>
          <div className="ml-4 flex gap-1">
            <Step n={1} label="Assembly sheets" />
            <Step n={2} label="Types to trace" />
            <Step n={3} label="Plans & heights" />
            <Step n={4} label="Result" />
          </div>
          <button
            onClick={onClose}
            className="ml-auto rounded px-2 py-1 text-gray-400 hover:bg-gray-800 hover:text-white"
          >
            ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4 text-xs text-gray-300">
          {/* ───────────── 1. assembly sheets ───────────── */}
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-gray-400">
                Which sheets show the{" "}
                <b className="text-gray-200">
                  wall, partition and ceiling types
                </b>
                ? These are usually the typical-details / assembly-schedule
                sheets (and the legend on a plan, if that's where they are).{" "}
                {scanning && (
                  <span className="text-orange-400">
                    Looking through the set…
                  </span>
                )}
              </p>
              <div className="grid grid-cols-1 gap-1 sm:grid-cols-2 lg:grid-cols-3">
                {pages.map((n) => (
                  <label
                    key={n}
                    className={`flex cursor-pointer items-center gap-2 rounded border px-2 py-1.5 ${asmPages.includes(n) ? "border-orange-600 bg-orange-950/40" : "border-gray-800 bg-gray-800/40 hover:border-gray-600"}`}
                  >
                    <input
                      type="checkbox"
                      checked={asmPages.includes(n)}
                      onChange={() => togglePage(n)}
                      className="accent-orange-600"
                    />
                    <span className="w-6 font-mono text-gray-500">{n}</span>
                    <span
                      className="min-w-0 flex-1 truncate"
                      title={pageLabel(n)}
                    >
                      {pageLabel(n)}
                    </span>
                    {kinds[n] && kinds[n] !== "other" && (
                      <span className="rounded bg-gray-700 px-1 text-[9px] uppercase text-gray-300">
                        {kinds[n] === "assembly" ? "types" : kinds[n]}
                      </span>
                    )}
                  </label>
                ))}
              </div>
              {readErr && (
                <div className="rounded border border-red-800 bg-red-950/40 p-2 text-red-300">
                  {readErr}
                </div>
              )}
              <div className="flex items-center gap-2">
                <button
                  onClick={doRead}
                  disabled={!!reading || !asmPages.length}
                  className="rounded bg-orange-600 px-3 py-1.5 font-semibold text-white hover:bg-orange-500 disabled:opacity-40"
                >
                  {reading ||
                    `Read ${asmPages.length || ""} sheet${asmPages.length === 1 ? "" : "s"}`}
                </button>
                {(assemblies || []).length > 0 && (
                  <button
                    onClick={() => setStep(2)}
                    className="rounded bg-gray-700 px-3 py-1.5 hover:bg-gray-600"
                  >
                    Use the {assemblies.length} types already read →
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ───────────── 2. types ───────────── */}
          {step === 2 && (
            <div className="space-y-3">
              {aiNote && (
                <div className="rounded border border-yellow-800 bg-yellow-950/30 p-2 text-yellow-300">
                  {aiNote}
                </div>
              )}
              <p className="text-gray-400">
                Tick the types to trace. The{" "}
                <b className="text-gray-200">tag</b> must match the mark used on
                the plans (e.g. W3, 2.2, C1) — edit it if the reading is off.
                Types read from a picture of the sheet are marked{" "}
                <span className="text-yellow-300">check</span>.
              </p>
              <div className="overflow-hidden rounded border border-gray-800">
                <table className="w-full text-left">
                  <thead className="bg-gray-800/80 text-[10px] uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="w-8 p-2">Trace</th>
                      <th className="w-20 p-2">Kind</th>
                      <th className="w-24 p-2">Tag</th>
                      <th className="p-2">
                        Name{" "}
                        <span className="normal-case text-gray-600">
                          (short)
                        </span>{" "}
                        · description{" "}
                        <span className="normal-case text-gray-600">
                          (what's in it)
                        </span>
                      </th>
                      <th className="w-28 p-2">Rating</th>
                      <th className="w-16 p-2">Sheet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(assemblies || []).map((a) => (
                      <React.Fragment key={a.id}>
                        <tr
                          className={`border-t border-gray-800 ${a.trace ? "bg-gray-800/30" : ""}`}
                        >
                          <td className="p-2">
                            <input
                              type="checkbox"
                              checked={!!a.trace}
                              disabled={
                                a.kind !== "wall" && a.kind !== "ceiling"
                              }
                              onChange={(e) =>
                                upd(a.id, { trace: e.target.checked })
                              }
                              className="accent-orange-600"
                              title={
                                a.kind !== "wall" && a.kind !== "ceiling"
                                  ? "Only walls and ceilings are traced"
                                  : ""
                              }
                            />
                          </td>
                          <td className="p-2">
                            <select
                              value={a.kind}
                              onChange={(e) =>
                                upd(a.id, { kind: e.target.value })
                              }
                              className="rounded bg-gray-800 px-1 py-0.5 text-[11px]"
                            >
                              {[
                                "wall",
                                "ceiling",
                                "floor",
                                "roof",
                                "other",
                              ].map((k) => (
                                <option key={k} value={k}>
                                  {k}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="p-2">
                            <input
                              value={a.tag || ""}
                              onChange={(e) =>
                                upd(a.id, {
                                  tag: e.target.value
                                    .toUpperCase()
                                    .replace(/\s+/g, ""),
                                })
                              }
                              placeholder="tag"
                              className="w-20 rounded border border-gray-700 bg-gray-800 px-1.5 py-0.5 font-mono"
                            />
                          </td>
                          <td className="p-2">
                            <div className="flex items-center gap-1.5">
                              <input
                                value={a.name || ""}
                                onChange={(e) =>
                                  upd(a.id, { name: e.target.value })
                                }
                                placeholder="Short name"
                                maxLength={60}
                                className="min-w-0 flex-1 rounded border border-transparent bg-transparent px-1 py-0.5 font-medium text-gray-100 hover:border-gray-700 focus:border-gray-600 focus:bg-gray-800"
                              />
                              {!a.verified && (
                                <span
                                  className="rounded bg-yellow-900/50 px-1 text-[9px] text-yellow-300"
                                  title="Read from an image of the sheet — check the tag and build-up"
                                >
                                  check
                                </span>
                              )}
                              <button
                                onClick={() =>
                                  setOpenRow(openRow === a.id ? null : a.id)
                                }
                                className="whitespace-nowrap rounded px-1 text-gray-500 hover:text-gray-200"
                                title="Edit the description"
                              >
                                {descOf(a)
                                  ? `${descOf(a).split("\n").length} item${descOf(a).split("\n").length === 1 ? "" : "s"}`
                                  : "add items"}{" "}
                                {openRow === a.id ? "▴" : "▾"}
                              </button>
                            </div>
                            {openRow !== a.id && (
                              <button
                                onClick={() => setOpenRow(a.id)}
                                title={
                                  descOf(a) ||
                                  "No items read — click to add them"
                                }
                                className={`mt-0.5 line-clamp-2 w-full px-1 text-left text-[11px] leading-snug ${descOf(a) ? "text-gray-500 hover:text-gray-300" : "italic text-amber-400/80"}`}
                              >
                                {descOf(a)
                                  ? descOf(a).split("\n").join(" · ")
                                  : "No description — click to add what's in this assembly"}
                              </button>
                            )}
                          </td>
                          <td className="p-2">
                            {[a.fireRating, a.stc && `STC ${a.stc}`]
                              .filter(Boolean)
                              .map((r) => (
                                <span
                                  key={r}
                                  className="mr-1 rounded bg-red-900/40 px-1 text-[10px] text-red-300"
                                >
                                  {r}
                                </span>
                              ))}
                          </td>
                          <td className="p-2 text-gray-500">
                            {a.page ? (
                              <button
                                onClick={() => onGoToPage(a.page)}
                                className="underline decoration-dotted hover:text-gray-200"
                              >
                                {a.page}
                              </button>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                        {openRow === a.id && (
                          <tr className="border-t border-gray-800/60 bg-gray-950/50">
                            <td />
                            <td colSpan={5} className="p-2">
                              <div className="mb-1 text-[10px] uppercase tracking-wide text-gray-500">
                                Description — every item in this assembly, one
                                per line
                              </div>
                              <textarea
                                value={a.description ?? descOf(a)}
                                onChange={(e) =>
                                  upd(a.id, {
                                    description: e.target.value,
                                    layers: e.target.value
                                      .split("\n")
                                      .map((x) => x.trim())
                                      .filter(Boolean),
                                  })
                                }
                                rows={Math.min(
                                  12,
                                  Math.max(3, descOf(a).split("\n").length + 1),
                                )}
                                className="w-full rounded border border-gray-700 bg-gray-900 p-1.5 font-mono text-[11px] text-gray-300"
                                placeholder={`1/2" GYPSUM BOARD\n3-5/8" STEEL STUDS @ 16" O.C.\n1/2" GYPSUM BOARD`}
                              />
                              {a.height && (
                                <div className="mt-1 text-gray-400">
                                  Height on sheet:{" "}
                                  <b className="text-gray-200">{a.height}</b>
                                </div>
                              )}
                              <div className="mt-1 flex gap-2">
                                {kindBadge(a.kind)}
                                <span className="text-gray-500">
                                  {a.source === "ai-image"
                                    ? "read from the sheet image"
                                    : a.source === "manual"
                                      ? "added by hand"
                                      : "read from the sheet text"}
                                </span>
                                <button
                                  onClick={() =>
                                    setAssemblies((prev) =>
                                      prev.filter((x) => x.id !== a.id),
                                    )
                                  }
                                  className="ml-auto text-red-400 hover:text-red-300"
                                >
                                  Remove
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                    {!(assemblies || []).length && (
                      <tr>
                        <td
                          colSpan={6}
                          className="p-4 text-center text-gray-500"
                        >
                          No types read. Add them by hand, or pick other sheets.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    setAssemblies((prev) => [
                      ...(prev || []),
                      {
                        id: uid(),
                        kind: "wall",
                        tag: "",
                        name: "New wall type",
                        description: "",
                        layers: [],
                        trace: true,
                        verified: true,
                        source: "manual",
                      },
                    ])
                  }
                  className="rounded bg-gray-700 px-2 py-1 hover:bg-gray-600"
                >
                  + Wall type
                </button>
                <button
                  onClick={() =>
                    setAssemblies((prev) => [
                      ...(prev || []),
                      {
                        id: uid(),
                        kind: "ceiling",
                        tag: "",
                        name: "New ceiling type",
                        description: "",
                        layers: [],
                        trace: true,
                        verified: true,
                        source: "manual",
                      },
                    ])
                  }
                  className="rounded bg-gray-700 px-2 py-1 hover:bg-gray-600"
                >
                  + Ceiling type
                </button>
                <span className="ml-auto text-gray-500">
                  {selWalls.length} wall · {selCeils.length} ceiling types to
                  trace
                </span>
                <button
                  onClick={() => setStep(3)}
                  className="rounded bg-orange-600 px-3 py-1.5 font-semibold text-white hover:bg-orange-500"
                >
                  Next: plans &amp; heights →
                </button>
              </div>
            </div>
          )}

          {/* ───────────── 3. plans & heights ───────────── */}
          {step === 3 && (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <section className="space-y-2">
                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Floor plans — walls
                </h3>
                <div className="flex flex-wrap items-center gap-2 rounded border border-gray-800 bg-gray-800/40 p-2">
                  <span>Typical floor height</span>
                  <input
                    value={S.defaultHeight || ""}
                    onChange={(e) => set({ defaultHeight: e.target.value })}
                    placeholder={`9'-0"`}
                    className="w-20 rounded border border-gray-700 bg-gray-900 px-1.5 py-0.5"
                  />
                  <button
                    onClick={() => {
                      const f = { ...(S.floor || {}) };
                      for (const k of Object.keys(f))
                        f[k] = { ...f[k], height: S.defaultHeight || "" };
                      set({ floor: f });
                    }}
                    className="rounded bg-gray-700 px-2 py-0.5 hover:bg-gray-600"
                  >
                    Apply to all floors
                  </button>
                  <span className="w-full text-[11px] text-gray-500">
                    Every wall starts at its floor's height; change single walls
                    after (click a wall).
                  </span>
                </div>
                <div className="max-h-72 space-y-1 overflow-y-auto pr-1">
                  {pages
                    .filter((n) => kinds[n] === "floor" || S.floor?.[n])
                    .map((n) => {
                      const p = S.floor?.[n];
                      const sc = scales[n];
                      return (
                        <div
                          key={n}
                          className={`rounded border px-2 py-1.5 ${p ? "border-orange-700/60 bg-gray-800/50" : "border-gray-800"}`}
                        >
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={!!p}
                              onChange={() => toggleFloor(n)}
                              className="accent-orange-600"
                            />
                            <span
                              className="min-w-0 flex-1 truncate"
                              title={pageLabel(n)}
                            >
                              {pageLabel(n)}
                            </span>
                            {p && (
                              <input
                                value={p.height ?? ""}
                                onChange={(e) =>
                                  setFloor(n, { height: e.target.value })
                                }
                                placeholder={S.defaultHeight || `height`}
                                className="w-16 rounded border border-gray-700 bg-gray-900 px-1 py-0.5"
                                title="Wall height on this floor"
                              />
                            )}
                          </div>
                          {p && (
                            <div className="mt-1 flex items-center gap-3 pl-6 text-[11px]">
                              <label className="flex items-center gap-1">
                                <input
                                  type="checkbox"
                                  checked={p.walls ?? true}
                                  onChange={(e) =>
                                    setFloor(n, { walls: e.target.checked })
                                  }
                                  className="accent-orange-600"
                                />
                                Walls
                              </label>
                              <label className="flex items-center gap-1">
                                <input
                                  type="checkbox"
                                  checked={!!p.areas}
                                  onChange={(e) =>
                                    setFloor(n, { areas: e.target.checked })
                                  }
                                  className="accent-orange-600"
                                />
                                Room areas
                              </label>
                              {sc && (
                                <span
                                  className={`ml-auto ${sc.ok ? "text-green-400" : "text-red-400"}`}
                                >
                                  {sc.ok
                                    ? `scale ${sc.label || "set"}`
                                    : "no scale"}
                                </span>
                              )}
                              {p.walls !== false && heightOf(p) && (
                                <span className="text-gray-500">
                                  {fmtFeetIn(heightOf(p))}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  <details className="text-[11px] text-gray-500">
                    <summary className="cursor-pointer">Other sheets…</summary>
                    {pages
                      .filter((n) => kinds[n] !== "floor" && !S.floor?.[n])
                      .map((n) => (
                        <label
                          key={n}
                          className="flex items-center gap-2 py-0.5"
                        >
                          <input
                            type="checkbox"
                            checked={false}
                            onChange={() => toggleFloor(n)}
                            className="accent-orange-600"
                          />
                          {pageLabel(n)}
                        </label>
                      ))}
                  </details>
                </div>
                <div className="space-y-1.5 rounded border border-gray-800 p-2">
                  <label className="flex items-center gap-2">
                    Walls with no type tag:
                    <select
                      value={S.untaggedWall || ""}
                      onChange={(e) => set({ untaggedWall: e.target.value })}
                      className="ml-auto rounded bg-gray-800 px-1 py-0.5"
                    >
                      <option value="">Best guess, else “Untagged wall”</option>
                      {wallTypes.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.tag ? `${a.tag} — ` : ""}
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex items-center gap-2">
                    Room areas go to:
                    <select
                      value={S.areaTarget || ""}
                      onChange={(e) => set({ areaTarget: e.target.value })}
                      className="ml-auto rounded bg-gray-800 px-1 py-0.5"
                    >
                      <option value="">Floor area (by room)</option>
                      {ceilTypes.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.tag ? `${a.tag} — ` : ""}
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={S.openFaces ?? true}
                      onChange={(e) => set({ openFaces: e.target.checked })}
                      className="accent-orange-600"
                    />
                    Also draw tagged walls in open areas (no closed room beside
                    them)
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={S.aiWalls ?? true}
                      onChange={(e) => set({ aiWalls: e.target.checked })}
                      className="accent-orange-600"
                    />
                    AI double-check: walls the tags don't settle are read by the
                    AI from the drawing (tags and arrows around the wall)
                  </label>
                  <div className="space-y-1">
                    <div className="text-gray-400">Wall lines</div>
                    <label className="flex items-center gap-2 pl-1">
                      <input
                        type="radio"
                        name="wlines"
                        checked={(S.wallLines || "centre") === "centre"}
                        onChange={() => set({ wallLines: "centre" })}
                        className="accent-orange-600"
                      />
                      One line per wall (centreline) — a wall shared by two
                      rooms is drawn once
                    </label>
                    <label className="flex items-center gap-2 pl-1">
                      <input
                        type="radio"
                        name="wlines"
                        checked={S.wallLines === "faces"}
                        onChange={() => set({ wallLines: "faces" })}
                        className="accent-orange-600"
                      />
                      Each room's own faces (a shared wall is drawn from both
                      sides)
                    </label>
                  </div>
                  <div className="space-y-1">
                    <div className="text-gray-400">
                      Washrooms, counts and corners
                    </div>
                    <label className="flex items-center gap-2 pl-1">
                      <input
                        type="checkbox"
                        checked={S.wetSeparate ?? true}
                        onChange={(e) => set({ wetSeparate: e.target.checked })}
                        className="accent-orange-600"
                      />
                      Washroom walls as their own assembly (same tag, other
                      board) — washroom, toilet, W/C, bath, shower
                    </label>
                    <label className="flex items-center gap-2 pl-1">
                      <input
                        type="checkbox"
                        checked={S.counts ?? true}
                        onChange={(e) => set({ counts: e.target.checked })}
                        className="accent-orange-600"
                      />
                      Count doors (single / double, sizes below) and windows —
                      markers on count assemblies (HM frames etc.)
                    </label>
                    {(S.counts ?? true) && (
                      <label className="flex items-center gap-2 pl-6 text-[11px]">
                        <input
                          type="checkbox"
                          checked={S.aiOpenings ?? true}
                          onChange={(e) =>
                            set({ aiOpenings: e.target.checked })
                          }
                          className="accent-orange-600"
                        />
                        AI double-check doors &amp; windows — confirms each one
                        and finds missed ones (door swings, D/W tags)
                      </label>
                    )}
                    <label className="flex items-center gap-2 pl-1">
                      <input
                        type="checkbox"
                        checked={S.cornerBead ?? true}
                        onChange={(e) => set({ cornerBead: e.target.checked })}
                        className="accent-orange-600"
                      />
                      Count outside corners (corner bead — corners × wall height
                      = LF). Door jambs are left out — the frame covers them.
                    </label>
                  </div>
                  <div className="space-y-1">
                    <div className="text-gray-400">Doors in a wall</div>
                    <label className="flex items-center gap-2 pl-1">
                      <input
                        type="radio"
                        name="doormode"
                        checked={(S.doorMode || "deduct") === "deduct"}
                        onChange={() => set({ doorMode: "deduct" })}
                        className="accent-orange-600"
                      />
                      Line runs through · door deducted (width × door height)
                    </label>
                    <div className="grid grid-cols-3 gap-2 pl-6 text-[11px] text-gray-400">
                      {[
                        ["doorWidth", "Single width", `3'-2"`],
                        ["doubleDoorWidth", "Double width", `6'-2"`],
                        ["doorHeight", "Height", `7'-0"`],
                      ].map(([k, label, ph]) => (
                        <label key={k} className="flex flex-col gap-0.5">
                          {label}
                          <input
                            value={S[k] ?? ""}
                            onChange={(e) => set({ [k]: e.target.value })}
                            placeholder={ph}
                            className={`w-full rounded border bg-gray-900 px-1 py-0.5 text-gray-100 ${S[k] && !parseFeetInput(S[k]) ? "border-red-600" : "border-gray-700"}`}
                          />
                        </label>
                      ))}
                      <span className="col-span-3 text-gray-500">
                        Doors are counted — and deducted when the line runs
                        through — at these sizes; the drawing only decides
                        single or double. Blank = the default shown.
                      </span>
                    </div>
                    <label className="flex items-center gap-2 pl-1">
                      <input
                        type="radio"
                        name="doormode"
                        checked={S.doorMode === "gap"}
                        onChange={() => set({ doorMode: "gap" })}
                        className="accent-orange-600"
                      />
                      Leave a gap in the line
                    </label>
                  </div>
                  <div className="space-y-1">
                    <div className="text-gray-400">Windows in a wall</div>
                    <label className="flex items-center gap-2 pl-1">
                      <input
                        type="radio"
                        name="winmode"
                        checked={(S.windowMode || "deduct") === "deduct"}
                        onChange={() => set({ windowMode: "deduct" })}
                        className="accent-orange-600"
                      />
                      Line runs through · window length deducted (net LF &amp;
                      SF)
                    </label>
                    {(S.windowMode || "deduct") === "deduct" && (
                      <label className="flex items-center gap-2 pl-6 text-[11px] text-gray-400">
                        SF deduction height
                        <input
                          value={S.windowHeight ?? ""}
                          onChange={(e) =>
                            set({ windowHeight: e.target.value })
                          }
                          placeholder="full wall"
                          className="w-20 rounded border border-gray-700 bg-gray-900 px-1 py-0.5"
                        />
                        <span className="text-gray-500">
                          blank = full wall height
                        </span>
                      </label>
                    )}
                    <label className="flex items-center gap-2 pl-1">
                      <input
                        type="radio"
                        name="winmode"
                        checked={S.windowMode === "gap"}
                        onChange={() => set({ windowMode: "gap" })}
                        className="accent-orange-600"
                      />
                      Leave a gap in the line (like doors)
                    </label>
                  </div>
                  <p className="text-[11px] text-gray-500">
                    Every wall of every room is drawn (same rooms as Detect
                    rooms). A tag anywhere on a wall line types it; no tag → the
                    type used for that wall thickness on the sheet (drawn
                    dashed), or “Untagged wall”. Wide open gaps (open plan,
                    corridors) are left out of the wall runs.
                  </p>
                </div>
              </section>

              <section className="space-y-2">
                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Reflected ceiling plans — ceilings
                </h3>
                <p className="text-[11px] text-gray-500">
                  Every room on the RCP gets a ceiling. Its type comes from the
                  tag or callout in the room; otherwise from the{" "}
                  <b className="text-gray-300">grid drawn in it</b> — 2'×4',
                  2'×2', or no grid (drywall). Rooms with no matching type go to
                  an <b className="text-gray-300">Untagged ceiling</b> (one per
                  grid) that you assign later. If the RCP's walls are hard to
                  read, take the rooms from the matching floor plan.
                </p>
                <div className="max-h-72 space-y-1 overflow-y-auto pr-1">
                  {pages
                    .filter((n) => kinds[n] === "rcp" || S.rcp?.[n])
                    .map((n) => {
                      const p = S.rcp?.[n];
                      const sc = scales[n];
                      return (
                        <div
                          key={n}
                          className={`rounded border px-2 py-1.5 ${p ? "border-sky-700/60 bg-gray-800/50" : "border-gray-800"}`}
                        >
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={!!p}
                              onChange={() => toggleRcp(n)}
                              className="accent-orange-600"
                            />
                            <span
                              className="min-w-0 flex-1 truncate"
                              title={pageLabel(n)}
                            >
                              {pageLabel(n)}
                            </span>
                            {sc && (
                              <span
                                className={
                                  sc.ok ? "text-green-400" : "text-red-400"
                                }
                              >
                                {sc.ok ? "scale" : "no scale"}
                              </span>
                            )}
                          </div>
                          {p && (
                            <label className="mt-1 flex items-center gap-2 pl-6 text-[11px]">
                              Rooms from
                              <select
                                value={p.from || "self"}
                                onChange={(e) =>
                                  setRcp(n, { from: e.target.value })
                                }
                                className="rounded bg-gray-800 px-1 py-0.5"
                              >
                                <option value="self">this RCP</option>
                                {pages
                                  .filter(
                                    (m) =>
                                      m !== n &&
                                      (kinds[m] === "floor" || S.floor?.[m]),
                                  )
                                  .map((m) => (
                                    <option key={m} value={String(m)}>
                                      {pageLabel(m)}
                                    </option>
                                  ))}
                              </select>
                            </label>
                          )}
                        </div>
                      );
                    })}
                  <details className="text-[11px] text-gray-500">
                    <summary className="cursor-pointer">Other sheets…</summary>
                    {pages
                      .filter((n) => kinds[n] !== "rcp" && !S.rcp?.[n])
                      .map((n) => (
                        <label
                          key={n}
                          className="flex items-center gap-2 py-0.5"
                        >
                          <input
                            type="checkbox"
                            checked={false}
                            onChange={() => toggleRcp(n)}
                            className="accent-orange-600"
                          />
                          {pageLabel(n)}
                        </label>
                      ))}
                  </details>
                </div>
                <div className="space-y-1 rounded border border-gray-800 p-2">
                  <div className="text-gray-400">
                    Rooms with no ceiling tag — by the grid drawn in them
                  </div>
                  {GRID_ROWS.map(([k, label]) => {
                    const auto = autoGridType(k);
                    return (
                      <label key={k} className="flex items-center gap-2 pl-1">
                        <span className="w-32 text-gray-300">{label}</span>
                        <select
                          value={gridChoice(k)}
                          onChange={(e) =>
                            set({
                              ceilGrid: {
                                ...(S.ceilGrid || {}),
                                [k]: e.target.value,
                              },
                            })
                          }
                          className="ml-auto max-w-[60%] rounded bg-gray-800 px-1 py-0.5"
                        >
                          <option value="auto">
                            {auto
                              ? `Auto: ${auto.tag ? `${auto.tag} — ` : ""}${auto.name}`
                              : "Auto: Untagged ceiling"}
                          </option>
                          <option value="">Untagged ceiling</option>
                          {ceilTypes.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.tag ? `${a.tag} — ` : ""}
                              {a.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    );
                  })}
                </div>
              </section>

              <div className="col-span-full flex items-center gap-2 border-t border-gray-800 pt-3">
                {problems.length > 0 && (
                  <div className="text-red-300">
                    {problems.slice(0, 3).join(" · ")}
                    {problems.length > 3
                      ? ` · +${problems.length - 3} more`
                      : ""}
                  </div>
                )}
                {running && (
                  <div className="text-orange-300">
                    {running.msg} ({running.done}/{running.total})
                  </div>
                )}
                <button
                  onClick={run}
                  disabled={!canRun || !!running}
                  className="ml-auto rounded bg-orange-600 px-4 py-1.5 font-semibold text-white hover:bg-orange-500 disabled:opacity-40"
                >
                  {running ? "Tracing…" : "Trace"}
                </button>
              </div>
            </div>
          )}

          {/* ───────────── 4. result ───────────── */}
          {step === 4 && result && (
            <div className="space-y-3">
              {result.error ? (
                <div className="rounded border border-red-800 bg-red-950/40 p-2 text-red-300">
                  {result.error}
                </div>
              ) : (
                <>
                  <div className="text-gray-300">
                    Drew <b className="text-white">{result.shapes}</b> shapes on{" "}
                    {result.pages.length} sheet
                    {result.pages.length === 1 ? "" : "s"}. They are ordinary
                    takeoff shapes now — edit, delete or change heights like any
                    other.
                  </div>
                  <table className="w-full text-left">
                    <thead className="text-[10px] uppercase text-gray-500">
                      <tr>
                        <th className="p-1.5">Sheet</th>
                        <th className="p-1.5">Rooms</th>
                        <th className="p-1.5">Walls traced</th>
                        <th className="p-1.5">Ceilings</th>
                        <th className="p-1.5">Not traced</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {result.pages.map((r) => (
                        <tr
                          key={`${r.page}-${r.kind}`}
                          className="border-t border-gray-800 align-top"
                        >
                          <td className="p-1.5">
                            {pageLabel(r.page)}
                            <div className="text-[10px] text-gray-500">
                              {r.kind === "rcp"
                                ? "RCP"
                                : `floor · ${fmtFeetIn(r.height)}`}
                            </div>
                          </td>
                          <td className="p-1.5">{r.rooms}</td>
                          <td className="p-1.5">
                            {Object.entries(r.walls || {}).map(([k, v]) => (
                              <div key={k}>
                                {k}: <b>{v.toFixed(0)} LF</b>
                              </div>
                            ))}
                          </td>
                          <td className="p-1.5">
                            {Object.entries(r.ceilings || {}).map(([k, v]) => (
                              <div key={k}>
                                {k}: <b>{v.toFixed(0)} SF</b>
                              </div>
                            ))}
                          </td>
                          <td className="p-1.5 text-yellow-300">
                            {r.untaggedLf > 0.5 && (
                              <div>
                                {r.untaggedLf.toFixed(0)} LF drawn as “Untagged
                                wall” — click a line to assign
                              </div>
                            )}
                            {r.aiLf > 0.5 && (
                              <div className="text-violet-300">
                                {r.aiLf.toFixed(0)} LF typed by the AI from the
                                tags on the drawing
                              </div>
                            )}
                            {r.openings &&
                              (r.openings.ok ||
                                r.openings.added ||
                                r.openings.flagged ||
                                r.openings.toCheck) > 0 && (
                                <div className="text-violet-300">
                                  Doors &amp; windows:{" "}
                                  {[
                                    r.openings.ok &&
                                      `${r.openings.ok} confirmed by AI`,
                                    r.openings.added &&
                                      `${r.openings.added} missed ones added`,
                                    (r.openings.flagged ||
                                      r.openings.toCheck) &&
                                      `${r.openings.flagged + r.openings.toCheck} to check (hollow markers)`,
                                  ]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </div>
                              )}
                            {r.aiNote && (
                              <div className="text-gray-400">{r.aiNote}</div>
                            )}
                            {r.checkLf > 0.5 && (
                              <div className="text-amber-200/80">
                                {r.checkLf.toFixed(0)} LF where the tags
                                disagree or are far off (dashed) — click a line
                                to approve, switch it or ask the AI
                              </div>
                            )}
                            {r.guessedLf > 0.5 && (
                              <div className="text-amber-200/80">
                                {r.guessedLf.toFixed(0)} LF typed from wall
                                thickness (dashed) — check
                              </div>
                            )}
                            {r.counts && (
                              <div className="text-gray-300">
                                {[
                                  r.counts.door &&
                                    `${r.counts.door} door${r.counts.door === 1 ? "" : "s"}`,
                                  r.counts.door2 && `${r.counts.door2} double`,
                                  r.counts.window &&
                                    `${r.counts.window} window${r.counts.window === 1 ? "" : "s"}`,
                                  r.counts.bead &&
                                    `${r.counts.bead} outside corner${r.counts.bead === 1 ? "" : "s"}`,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}{" "}
                                counted
                              </div>
                            )}
                            {r.wetLf > 0.5 && (
                              <div className="text-sky-300">
                                {r.wetLf.toFixed(0)} LF washroom walls (own
                                assemblies)
                              </div>
                            )}
                            {r.untypedSf > 0.5 && (
                              <div>
                                {r.untypedSf.toFixed(0)} SF drawn as “Untagged
                                ceiling” — click an area to assign
                              </div>
                            )}
                            {r.gridSf > 0.5 && (
                              <div className="text-gray-400">
                                {r.gridSf.toFixed(0)} SF typed from the grid
                                drawn in the room
                              </div>
                            )}
                            {r.doorsLf > 0.5 && (
                              <div className="text-gray-500">
                                {r.doorsLf.toFixed(0)} LF of doors crossed &amp;
                                deducted
                              </div>
                            )}
                            {r.openLf > 0.5 && (
                              <div className="text-gray-400">
                                incl. {r.openLf.toFixed(0)} LF on open-area
                                walls — check
                              </div>
                            )}
                            {r.note && <div>{r.note}</div>}
                          </td>
                          <td className="p-1.5">
                            <button
                              onClick={() => {
                                onGoToPage(r.page);
                                onClose();
                              }}
                              className="rounded bg-gray-700 px-2 py-0.5 hover:bg-gray-600"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-[11px] text-gray-500">
                    Click any line to change its assembly or height, or press
                    Delete to remove it. A room that didn't close can be filled
                    with <b>Fill room</b> or drawn by hand. The whole trace can
                    be undone from the Assemblies tab.
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
